import type { CompanyReport, SegmentReport } from "../types"

export type PlanLibraryDoc = { id: string; title: string }

function titleFromUrl(url: string): string {
  try {
    const host = new URL(url).hostname.replace(/^www\./, "")
    return host || url
  } catch {
    return url
  }
}

function addDoc(input: { docs: PlanLibraryDoc[]; seen: Set<string>; id: string; title: string }) {
  const key = input.id.trim()
  if (!key || input.seen.has(key)) return
  input.seen.add(key)
  input.docs.push({ id: key, title: input.title.trim() || key })
}

/** Collect selectable source docs from a segment report's URL/evidence lists. */
export function libraryDocsFromSegmentReport(
  report: SegmentReport | null | undefined
): PlanLibraryDoc[] {
  if (!report) return []
  const docs: PlanLibraryDoc[] = []
  const seen = new Set<string>()
  const market = report.marketOverview
  for (const url of market.marketSize.sources) {
    addDoc({ docs, seen, id: url, title: `Segment market size · ${titleFromUrl(url)}` })
  }
  for (const url of market.marketGrowth.sources) {
    addDoc({ docs, seen, id: url, title: `Segment market growth · ${titleFromUrl(url)}` })
  }
  for (const [label, meta] of Object.entries(market.sections ?? {})) {
    for (const url of meta.sources ?? []) {
      addDoc({ docs, seen, id: url, title: `Segment ${label} · ${titleFromUrl(url)}` })
    }
  }
  for (const item of market.newsFeed ?? []) {
    if (item.url) {
      addDoc({
        docs,
        seen,
        id: item.url,
        title: item.headline || titleFromUrl(item.url),
      })
    }
  }
  return docs
}

/** Collect selectable source docs from a company report profile / moves / claims. */
export function libraryDocsFromCompanyReport(
  report: CompanyReport | null | undefined
): PlanLibraryDoc[] {
  if (!report) return []
  const docs: PlanLibraryDoc[] = []
  const seen = new Set<string>()
  for (const url of report.profile?.sources ?? []) {
    addDoc({ docs, seen, id: url, title: `Company profile · ${titleFromUrl(url)}` })
  }
  for (const move of report.recentMoves ?? []) {
    for (const url of move.sources ?? []) {
      const label = move.title
        ? `${move.title} · ${titleFromUrl(url)}`
        : `Company move · ${titleFromUrl(url)}`
      addDoc({ docs, seen, id: url, title: label })
    }
  }
  for (const claim of report.grounded?.claims ?? []) {
    for (const cite of claim.citations ?? []) {
      const quote = cite.quote.trim()
      addDoc({
        docs,
        seen,
        id: cite.sourceId,
        title: quote ? quote.slice(0, 80) : `Claim source · ${cite.sourceId}`,
      })
    }
  }
  return docs
}

/** Merge company + segment library docs, de-duplicating by id. */
export function mergePlanLibraryDocs(
  companyReport: CompanyReport | null | undefined,
  segmentReport: SegmentReport | null | undefined
): PlanLibraryDoc[] {
  const seen = new Set<string>()
  const merged: PlanLibraryDoc[] = []
  for (const doc of [
    ...libraryDocsFromCompanyReport(companyReport),
    ...libraryDocsFromSegmentReport(segmentReport),
  ]) {
    if (seen.has(doc.id)) continue
    seen.add(doc.id)
    merged.push(doc)
  }
  return merged
}
